import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [role, setRole] = useState(localStorage.getItem('role'));
  const [loading, setLoading] = useState(!localStorage.getItem('token')); // Skip loading if token already exists
  const [auth0Config, setAuth0Config] = useState(null);
  
  // Auth0 hooks
  const {
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0IsLoading,
    user: auth0User,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  // Fetch Auth0 config from backend
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/auth/config`);
        setAuth0Config(response.data);
      } catch (error) {
        console.error('Failed to fetch auth config:', error);
        setAuth0Config({ auth0_enabled: false });
      }
    };
    fetchConfig();
  }, []);

  // Handle Auth0 authentication
  useEffect(() => {
    const handleAuth0Login = async () => {
      if (auth0IsAuthenticated && auth0User && !token) {
        try {
          // Get Auth0 access token
          const accessToken = await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            }
          });
          
          // Exchange Auth0 token for our internal token
          const userType = localStorage.getItem('pending_user_type') || 'chro';
          const response = await axios.post(`${API}/auth/auth0/callback`, null, {
            params: { token: accessToken, user_type: userType }
          });
          
          const { token: newToken, user: userData, role: userRole } = response.data;
          
          localStorage.setItem('token', newToken);
          localStorage.setItem('role', userRole);
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.removeItem('pending_user_type');
          
          setToken(newToken);
          setUser(userData);
          setRole(userRole);
        } catch (error) {
          console.error('Auth0 callback failed:', error);
        }
      }
      setLoading(false);
    };

    if (!auth0IsLoading) {
      handleAuth0Login();
    }
  }, [auth0IsAuthenticated, auth0User, auth0IsLoading, token, getAccessTokenSilently]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Mock auth login (for demo without Auth0)
  const adminLogin = async (email, password) => {
    const response = await axios.post(`${API}/auth/admin/login`, { email, password });
    const { token: newToken, user: userData, role: userRole } = response.data;
    
    localStorage.setItem('token', newToken);
    localStorage.setItem('role', userRole);
    localStorage.setItem('user', JSON.stringify(userData));
    
    setToken(newToken);
    setUser(userData);
    setRole(userRole);
    
    return response.data;
  };

  const chroLogin = async (email, password) => {
    const response = await axios.post(`${API}/auth/chro/login`, { email, password });
    const { token: newToken, user: userData, role: userRole } = response.data;

    // Clear any prior user's analysis session state
    localStorage.removeItem('reai_session_id');
    localStorage.removeItem('analysis_session_id');
    localStorage.removeItem('analysis_function');
    localStorage.removeItem('analysis_roles');
    localStorage.removeItem('analysis_discipline');
    localStorage.removeItem('analysis_mol_results');
    localStorage.removeItem('demo_searches');

    localStorage.setItem('token', newToken);
    localStorage.setItem('role', userRole);
    localStorage.setItem('user', JSON.stringify(userData));
    
    setToken(newToken);
    setUser(userData);
    setRole(userRole);
    
    return response.data;
  };

  // Auth0 login
  const loginWithAuth0 = useCallback((userType = 'chro') => {
    localStorage.setItem('pending_user_type', userType);
    loginWithRedirect({
      authorizationParams: {
        redirect_uri: `${window.location.origin}/callback`,
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      }
    });
  }, [loginWithRedirect]);

  const setPasswordFromInvite = async (inviteToken, password) => {
    await axios.post(`${API}/auth/chro/set-password`, null, {
      params: { invite_token: inviteToken, password }
    });
  };

  const verifyInviteToken = async (inviteToken) => {
    const response = await axios.get(`${API}/auth/verify-invite/${inviteToken}`);
    return response.data;
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('user');
    localStorage.removeItem('is_demo');
    localStorage.removeItem('demo_org_name');
    localStorage.removeItem('demo_sector');
    localStorage.removeItem('demo_signal_data');
    // Clear analysis session keys so next user never inherits state
    localStorage.removeItem('reai_session_id');
    localStorage.removeItem('analysis_session_id');
    localStorage.removeItem('analysis_function');
    localStorage.removeItem('analysis_roles');
    localStorage.removeItem('analysis_discipline');
    localStorage.removeItem('analysis_mol_results');
    // Clear Stage cache
    localStorage.removeItem('demo_searches');
    setToken(null);
    setUser(null);
    setRole(null);
    delete axios.defaults.headers.common['Authorization'];
    
    // If Auth0 is configured and user is logged in via Auth0, log out from Auth0 too
    if (auth0Config?.auth0_enabled && auth0IsAuthenticated) {
      auth0Logout({
        logoutParams: {
          returnTo: window.location.origin,
        }
      });
    }
  }, [auth0Config, auth0IsAuthenticated, auth0Logout]);

  const value = {
    user,
    token,
    role,
    loading: loading || auth0IsLoading,
    isAuthenticated: !!token,
    isAdmin: role === 'admin' || role === 'super_admin' || role === 'operator',
    isChro: role === 'chro' || role === 'demo',
    isDemoUser: role === 'demo',
    auth0Config,
    auth0IsAuthenticated,
    adminLogin,
    chroLogin,
    loginWithAuth0,
    setPasswordFromInvite,
    verifyInviteToken,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
